import {
  breakpointSizes,
  getBreakpointDown,
  getBreakpointOnly,
  getBreakpointUp,
  getFontCss,
  styleConstants,
} from '@paywithextend/components'
import React, { useEffect, useRef, useState } from 'react'
import BgVirtualCardMobile from 'src/assets/images/bg-virtualcard-mobile.svg'
import BgVirtualCardWeb from 'src/assets/images/bg-virtualcard-web.svg'
import BgVirtualCardWebGrayscale from 'src/assets/images/bg-virtualcard-web-grayscale.svg'
import SvgGraphicCardExtendGreen from 'src/assets/images/graphic-cardTile-Active.png'
import PendingCardTileSvg from 'src/assets/images/graphic-cardTile-Pending.png'
import { useTranslate } from 'src/localization/Trns'
import { UserListModel } from 'src/models/user.models'
// models
import { VirtualCardSecureModel } from 'src/models/virtualCard.models'
import styled, { css, CSSProp } from 'styled-components'

interface VirtualCardDetailImageProps {
  className?: string
  recipient: UserListModel | undefined
  virtualCard: VirtualCardSecureModel
  hideCardDetails?: boolean
  hideCardholderName?: boolean
  grayscaleImage?: boolean
}

const CardBgImage = styled.img`
  position: absolute;
  top: 50%;
  width: 100%;
`

const StyledImg = styled.img<{
  hidden?: boolean
}>`
  flex: 0 0 auto;
  width: 100%;
  border-radius: 3.37%/5.92%;
  z-index: 0;
  box-shadow: 0 4px 10px 0 rgba(0, 0, 0, 0.15);
  ${({ hidden }): CSSProp => {
    return hidden
      ? css`
          display: none;
        `
      : css``
  }}
`

const ContentDiv = styled.div<{
  isActive: boolean
}>`
  position: absolute;
  bottom: 0;
  left: 0;
  z-index: 1;
  height: 106px;
  width: 100%;
  padding-left: 16px;
  padding-bottom: 16px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  text-shadow: 1px 2px 0 rgba(0, 0, 0, 0.15);
  ${getFontCss(
    20,
    styleConstants.colors.white,
    styleConstants.fontFamilies.regular,
  )};
  overflow: hidden;

  ${getBreakpointDown(breakpointSizes.MD)(css`
    height: 100px;
    ${getFontCss(
      16,
      styleConstants.colors.white,
      styleConstants.fontFamilies.regular,
    )};
  `)}

  ${getBreakpointOnly(breakpointSizes.XSM)(css`
    justify-content: space-between;
    height: 60px;
    ${getFontCss(
      16,
      styleConstants.colors.white,
      styleConstants.fontFamilies.medium,
    )};
  `)}

  ${({ isActive }): CSSProp => {
    return isActive
      ? css`
          height: 116px;
          @media (max-width: 350px) {
            height: 70px;
            margin-bottom: 12px;
          }
        `
      : css``
  }}
`

const InfoDiv = styled.div`
  display: flex;
  flex-direction: column;
  width: 220px;
  justify-content: space-between;
  ${getBreakpointOnly(breakpointSizes.XSM)(css`
    &:first-child {
      flex-shrink: 2;
    }

    &:last-child {
      flex-grow: 2;
    }
  `)}
`

const InfoHeaderSpan = styled.span<{
  color: string
}>`
  ${({ color }): CSSProp => css`
    ${getFontCss(12, color, styleConstants.fontFamilies.regular)};
  `}

  opacity: 0.75;
`

const InfoBodySpan = styled.span<{
  color: string
}>`
  ${({ color }): CSSProp => css`
    ${getFontCss(14, color, styleConstants.fontFamilies.regular)};
  `}
  margin-top: 2px;
  ${getBreakpointDown(breakpointSizes.XSM)(css`
    line-height: 20px;
  `)}
`

const VirtualCardDetailImage: React.FC<VirtualCardDetailImageProps> = (
  props,
) => {
  const translate = useTranslate()

  const textColorStyle = { color: props.virtualCard.cardImage.textColorRGBA }
  const textShadowStyle = props.virtualCard.cardImage.hasTextShadow
    ? {
        textShadow: `1px 1px 4px ${props.virtualCard.cardImage.shadowTextColorRGBA}, -1px -1px 4px ${props.virtualCard.cardImage.shadowTextColorRGBA}`,
      }
    : {}

  const imageRef = useRef<HTMLImageElement | null>(null)
  const [isImageLoaded, setIsImageLoaded] = useState(false)
  useEffect(() => {
    if (imageRef.current !== null && imageRef.current.complete) {
      setIsImageLoaded(true)
    }
  }, [imageRef])
  const onImageLoad = (): void => {
    setIsImageLoaded(true)
  }

  return (
    <div
      className={props.className}
      css={css`
        width: 100%;
        display: flex;
        flex-direction: column;
        justify-content: flex-end;
        position: relative;
      `}
    >
      <CardBgImage
        css={css`
          transform-origin: left;
          transform: translateY(-50%)
            ${props.grayscaleImage ? css`scale(1.03);` : css`scale(0.93);`};
          ${getBreakpointDown(breakpointSizes.XSM)(css`
            display: none;
          `)}
        `}
        src={
          props.grayscaleImage ? BgVirtualCardWebGrayscale : BgVirtualCardWeb
        }
        alt={''}
      />
      {!props.grayscaleImage && (
        <CardBgImage
          css={css`
            transform: translateY(-50%) scale(1.03);
            ${getBreakpointUp(breakpointSizes.SM)(css`
              display: none;
            `)}
          `}
          src={BgVirtualCardMobile}
          alt={''}
        />
      )}
      {!isImageLoaded && <StyledImg src={SvgGraphicCardExtendGreen} alt={''} />}
      <StyledImg
        ref={imageRef}
        hidden={!isImageLoaded}
        onLoad={onImageLoad}
        src={
          props.virtualCard.isPending
            ? PendingCardTileSvg
            : props.virtualCard.cardImage.urls.large
        }
        alt={''}
      />
      {isImageLoaded && (
        <ContentDiv
          isActive={props.virtualCard.pseudoStatusModifiers['isActive']}
          style={{ ...textColorStyle, ...textShadowStyle }}
        >
          {!props.hideCardholderName && props.recipient && (
            <span
              css={css`
                overflow: hidden;
                white-space: nowrap;
                text-overflow: ellipsis;
                ${getBreakpointDown(breakpointSizes.XSM)(css`
                  display: none;
                `)}
              `}
            >
              {props.recipient.cardImageFullName}
            </span>
          )}
          <span
            css={css`
              white-space: nowrap;
              ${getBreakpointOnly(breakpointSizes.XXSM)(css`
                display: none;
              `)}
            `}
          >
            {props.hideCardDetails
              ? props.virtualCard.redactedVcn
              : props.virtualCard.formattedCardNumber}
          </span>
          <span
            css={css`
              white-space: nowrap;
              ${getBreakpointUp(breakpointSizes.XSM)(css`
                display: none;
              `)}
            `}
          >
            {props.virtualCard.truncatedDisplayName}
          </span>
          <span
            css={css`
              ${getFontCss(
                24,
                props.virtualCard.cardImage.textColorRGBA,
                styleConstants.fontFamilies.regular,
              )};
              ${getBreakpointUp(breakpointSizes.XSM)(css`
                display: none;
              `)}
            `}
          >
            {props.virtualCard.balanceLabel}
          </span>
          {!props.virtualCard.isPending && (
            <div
              css={css`
                display: flex;
                ${getBreakpointOnly(breakpointSizes.XXSM)(css`
                  display: none;
                `)}
              `}
            >
              <InfoDiv>
                <InfoHeaderSpan
                  color={props.virtualCard.cardImage.textColorRGBA}
                >
                  {translate('Expires')}
                </InfoHeaderSpan>
                <InfoBodySpan color={props.virtualCard.cardImage.textColorRGBA}>
                  {props.hideCardDetails
                    ? 'XX/XX'
                    : props.virtualCard.expirationString}
                </InfoBodySpan>
              </InfoDiv>
              {props.virtualCard.isActive &&
                props.virtualCard.securityCode !== undefined && (
                  <InfoDiv>
                    <InfoHeaderSpan
                      color={props.virtualCard.cardImage.textColorRGBA}
                    >
                      {translate('CVC')}
                    </InfoHeaderSpan>
                    <InfoBodySpan
                      color={props.virtualCard.cardImage.textColorRGBA}
                    >
                      {props.hideCardDetails
                        ? 'XXX'
                        : props.virtualCard.securityCode}
                    </InfoBodySpan>
                  </InfoDiv>
                )}
            </div>
          )}
          {props.virtualCard.isPending && <span>{translate('Pending')}</span>}
        </ContentDiv>
      )}
    </div>
  )
}

export default VirtualCardDetailImage
